import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FileDropzone } from '../../../../components/FileInput'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { LoadingBlock } from '../../../../components/Loading'
import { StatCard } from '../../../../components/SectionCard'
import {
  ApiErrorShape,
  formatLocalDateTime,
  nowSaoPauloDateTimeLocal,
  toFormErrors,
  useAppToast
} from '../../../../helpers'
import { EventService } from '../../../../services/Event/EventService'
import {
  CertificateFieldKey,
  CertificateLayout,
  EventCertificate,
  EventCertificatesStats
} from '../../../../services/Event/types'
import { renderEmailSample, SAMPLE_RECIPIENT_NAME } from '../survey/eventSurveyForm'
import CertificateLayoutEditor from './CertificateLayoutEditor'
import { IPdfFirstPage, openPdfFirstPage, scaleLayout } from './certificateRendering'

interface IEventCertificatesModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: number
  eventName: string
  onConfigureSurvey?: () => void
}

/** Tamanho dos certificados do Canva, em que o layout padrão da API foi medido. */
const REFERENCE_PAGE = { width: 876.27, height: 629.52 }

const SAMPLE_NAME = 'Nome do Participante'

const DEFAULT_SUBJECT = 'Certificado de Participação — #NOME_EVENTO'

// #LINK_PESQUISA vira o link individual da pesquisa NPS do evento (ABRA-370): cada participante
// recebe o próprio, e a API recusa o envio se o evento não tiver pesquisa ativa.
const DEFAULT_BODY = `Boa tarde, #PRIMEIRO_NOME! tudo bem?

Foi um prazer ter você conosco aqui no #NOME_EVENTO da ABRA!

Estamos encaminhando em anexo o seu certificado de participação.

Também gostaríamos de conhecer um pouco mais sobre a sua experiência durante a oficina. Preparamos uma pesquisa rápida de satisfação, que leva aproximadamente 1 minuto para ser respondida e será muito importante para continuarmos aprimorando nossos próximos encontros.

Pesquisa de satisfação:
#LINK_PESQUISA

Agradecemos pela sua participação e esperamos receber você novamente em nossas próximas oficinas!

Atenciosamente,`

const EMPTY_TEMPLATE_ERRORS = { template: '', layout: '' }
const EMPTY_SEND_ERRORS = { subject: '', body: '', scheduled_at: '' }

const TAB_TEMPLATE = 0
const TAB_SEND = 1

/**
 * Certificados de participação do evento (ABRA-343).
 *
 * **Modelo**: o PDF exportado do Canva — que já traz o nome do evento e a data — e o editor que diz
 * onde entra o nome do participante. **Envio**: o mesmo fluxo dos certificados de expositor
 * dos concursos — contadores, assunto e corpo do e-mail, reenvio opcional —, para quem pagou e fez
 * check-in.
 *
 * O envio pode ser agendado (ABRA-364) — um agendamento pendente por evento, que pode ser editado ou
 * cancelado. Nada sai antes do término do evento: até lá, só dá para agendar, a partir desse horário.
 */
export default function EventCertificatesModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  onConfigureSurvey
}: IEventCertificatesModalProps) {
  const toast = useAppToast()
  const eventService = useMemo(() => new EventService(), [])
  const confirmDialog = useDisclosure()
  const cancelScheduleDialog = useDisclosure()

  const [tabIndex, setTabIndex] = useState(TAB_TEMPLATE)
  const [loading, setLoading] = useState(false)
  const [certificate, setCertificate] = useState<EventCertificate | null>(null)
  const [page, setPage] = useState<IPdfFirstPage | null>(null)
  const [newTemplate, setNewTemplate] = useState<File | null>(null)
  const [layout, setLayout] = useState<CertificateLayout | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [sampleName, setSampleName] = useState(SAMPLE_NAME)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [templateErrors, setTemplateErrors] = useState(EMPTY_TEMPLATE_ERRORS)

  const [stats, setStats] = useState<EventCertificatesStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsError, setStatsError] = useState(false)
  const [force, setForce] = useState(false)
  const [subject, setSubject] = useState(DEFAULT_SUBJECT)
  const [body, setBody] = useState(DEFAULT_BODY)
  const [showEmailPreview, setShowEmailPreview] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendErrors, setSendErrors] = useState(EMPTY_SEND_ERRORS)
  const [isScheduling, setIsScheduling] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')

  const hasSavedTemplate = !!certificate?.template
  const pendingSchedule = certificate?.schedule ?? null

  // Horários de parede, no formato do datetime-local — comparar as strings já compara as datas.
  const endsAt = certificate?.ends_at?.slice(0, 16) ?? null
  const now = nowSaoPauloDateTimeLocal()
  const hasEventEnded = !endsAt || endsAt <= now
  const minScheduledAt = endsAt && endsAt > now ? endsAt : now
  // Antes do término do evento não há envio imediato: só o agendamento.
  const scheduling = isScheduling || !hasEventEnded
  // O `min` do datetime-local não impede digitar ou colar uma data anterior.
  const scheduledAtError =
    sendErrors.scheduled_at ||
    (scheduledAt && scheduledAt < minScheduledAt
      ? endsAt && endsAt > now
        ? `O envio só pode ser agendado a partir de ${formatLocalDateTime(endsAt)} (término do evento).`
        : 'A data de agendamento deve ser no futuro.'
      : '')

  // Troca a página aberta liberando a anterior — o pdf.js segura o documento na memória.
  const replacePage = useCallback((next: IPdfFirstPage | null) => {
    setPage((current) => {
      if (current && current !== next) current.destroy()
      return next
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined

    let active = true
    setLoading(true)

    eventService
      .getCertificate(eventId)
      .then(async (data) => {
        if (!active) return
        setCertificate(data)
        setLayout(data.layout ?? data.default_layout)

        // Já agendado: o formulário abre com o agendamento, para editar. Senão, o horário sugerido é o
        // término do evento.
        if (data.schedule) {
          setSubject(data.schedule.subject)
          setBody(data.schedule.body)
          setForce(data.schedule.force)
          setIsScheduling(true)
          setScheduledAt(data.schedule.scheduled_at.slice(0, 16))
        } else if (data.ends_at) {
          setScheduledAt(data.ends_at.slice(0, 16))
        }

        if (data.template) {
          const opened = await openPdfFirstPage(await eventService.getCertificateTemplate(eventId))
          if (active) replacePage(opened)
          else opened.destroy()
        }
      })
      .catch((err) => {
        if (active) toast.error('Erro ao carregar o certificado do evento!', err)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [isOpen, eventId, eventService, replacePage, toast])

  const loadStats = useCallback(() => {
    let active = true
    setStatsError(false)
    setStatsLoading(true)

    eventService
      .getCertificatesPreview(eventId, force)
      .then((data) => {
        if (active) setStats(data)
      })
      .catch((err) => {
        if (!active) return
        setStatsError(true)
        setStats(null)
        toast.error('Erro ao carregar os destinatários!', err)
      })
      .finally(() => {
        if (active) setStatsLoading(false)
      })

    return () => {
      active = false
    }
  }, [eventId, force, eventService, toast])

  useEffect(() => {
    if (!isOpen || tabIndex !== TAB_SEND) return undefined
    return loadStats()
  }, [isOpen, tabIndex, loadStats])

  const handleClose = () => {
    replacePage(null)
    setTabIndex(TAB_TEMPLATE)
    setCertificate(null)
    setNewTemplate(null)
    setLayout(null)
    setIsDirty(false)
    setSampleName(SAMPLE_NAME)
    setTemplateErrors(EMPTY_TEMPLATE_ERRORS)
    setStats(null)
    setStatsError(false)
    setForce(false)
    setSubject(DEFAULT_SUBJECT)
    setBody(DEFAULT_BODY)
    setShowEmailPreview(false)
    setSendErrors(EMPTY_SEND_ERRORS)
    setIsScheduling(false)
    setScheduledAt('')
    onClose()
  }

  const handleSchedulingChange = (checked: boolean) => {
    setIsScheduling(checked)
    if (checked && (!scheduledAt || scheduledAt < minScheduledAt)) setScheduledAt(minScheduledAt)
  }

  const handleTemplateChosen = async (files: File[]) => {
    const file = files[files.length - 1]
    if (!file) return

    setTemplateErrors(EMPTY_TEMPLATE_ERRORS)

    try {
      const opened = await openPdfFirstPage(await file.arrayBuffer())
      replacePage(opened)
      setNewTemplate(file)
      setIsDirty(true)

      // Primeiro modelo do evento: o layout padrão, levado ao tamanho desta página. Na troca de
      // modelo o layout já ajustado continua — é o que a API faz também.
      if (!hasSavedTemplate && certificate) {
        setLayout(scaleLayout(certificate.default_layout, REFERENCE_PAGE, opened))
      }
    } catch {
      setTemplateErrors({ ...EMPTY_TEMPLATE_ERRORS, template: 'Não foi possível ler este PDF.' })
    }
  }

  const handleLayoutChange = (next: CertificateLayout) => {
    setLayout(next)
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!layout) return
    setSaving(true)
    setTemplateErrors(EMPTY_TEMPLATE_ERRORS)

    try {
      const saved = await eventService.saveCertificate(eventId, { template: newTemplate, layout })
      setCertificate(saved)
      if (saved.layout) setLayout(saved.layout)
      setNewTemplate(null)
      setIsDirty(false)
      toast.success('Modelo do certificado salvo!')
    } catch (err) {
      setTemplateErrors(toFormErrors(EMPTY_TEMPLATE_ERRORS, (err as ApiErrorShape).response?.data?.errors))
      toast.error('Erro ao salvar o modelo do certificado!', err)
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = async () => {
    if (!layout) return
    setPreviewing(true)

    try {
      await eventService.openCertificatePreviewPdf(eventId, { template: newTemplate, layout })
    } catch (err) {
      toast.error('Erro ao gerar a pré-visualização do certificado!', err)
    } finally {
      setPreviewing(false)
    }
  }

  const handleSend = async () => {
    setSending(true)
    setSendErrors(EMPTY_SEND_ERRORS)

    try {
      if (scheduling) {
        await eventService.saveCertificateSchedule(eventId, { subject, body, force, scheduled_at: scheduledAt })

        toast.success(
          'Envio de certificados agendado!',
          `Será enviado em ${formatLocalDateTime(scheduledAt)} a quem tiver feito check-in até lá.`
        )
      } else {
        const result = await eventService.sendCertificates(eventId, { subject, body, force })

        toast.success(
          'Envio de certificados iniciado!',
          `${result.recipients} participante(s) vão receber o certificado de participação por e-mail.`
        )
      }

      confirmDialog.onClose()
      handleClose()
    } catch (err) {
      confirmDialog.onClose()
      setSendErrors(toFormErrors(EMPTY_SEND_ERRORS, (err as ApiErrorShape).response?.data?.errors))
      toast.error(scheduling ? 'Erro ao agendar o envio dos certificados!' : 'Erro ao enviar os certificados!', err)
    } finally {
      setSending(false)
    }
  }

  const handleCancelSchedule = async () => {
    try {
      await eventService.cancelCertificateSchedule(eventId)
      setCertificate((current) => (current ? { ...current, schedule: null } : current))
      setIsScheduling(false)
      // Sem o horário cancelado: antes do término o agendamento continua ligado, e o campo volta
      // para o horário sugerido em vez de repetir o que acabou de ser cancelado.
      setScheduledAt(minScheduledAt)
      toast.success('Agendamento cancelado!', 'Os certificados não serão mais enviados nesse horário.')
    } catch (err) {
      toast.error('Erro ao cancelar o agendamento!', err)
    } finally {
      cancelScheduleDialog.onClose()
    }
  }

  // Memoizado: o editor refaz a medida e o desenho dos textos quando este objeto muda.
  const texts = useMemo<Record<CertificateFieldKey, string>>(() => ({ name: sampleName }), [sampleName])

  const isSendFilled = subject.trim() !== '' && body.trim() !== ''
  const usesSurveyLink = body.includes('#LINK_PESQUISA')
  // Só bloqueia com a situação da pesquisa conhecida: sem os contadores (carregando ou erro) o
  // aviso nem aparece, e o agendamento — que não depende deles — ficaria travado sem explicação.
  // A API recusa o envio e o agendamento com o link e a pesquisa inativa de qualquer jeito.
  const surveyUnavailable = usesSurveyLink && !!stats && stats.survey_status !== 'active'
  const sampleText = (value: string) => renderEmailSample(value, eventName, stats?.survey_example_url)
  // O agendamento não depende dos contadores: antes do evento ainda não há check-in, e os
  // destinatários são calculados no horário do envio.
  const canSend = scheduling
    ? hasSavedTemplate && isSendFilled && scheduledAt >= minScheduledAt && !surveyUnavailable && !sending
    : hasSavedTemplate &&
      !!stats &&
      stats.recipients > 0 &&
      isSendFilled &&
      !surveyUnavailable &&
      !statsLoading &&
      !sending

  const templateTab = (
    <Flex direction="column" gap={4}>
      <Text variant="caption">
        Envie o certificado exportado do Canva (PDF), já com o nome do evento e a data, e posicione o nome do
        participante. Cada evento tem o seu modelo.
      </Text>

      {/* A lista fica vazia de propósito: cada PDF escolhido substitui o anterior, e o nome dele
          aparece no texto de apoio. Com o arquivo na lista, o limite de 1 recusaria a troca. */}
      <FileDropzone
        accept="certificateTemplate"
        files={[]}
        onChange={handleTemplateChosen}
        maxSizeMB={20}
        maxFiles={1}
        label={hasSavedTemplate || newTemplate ? 'Trocar modelo' : 'Modelo do certificado'}
        title={
          hasSavedTemplate || newTemplate
            ? 'Arraste outro PDF ou clique aqui para trocar'
            : 'Arraste o PDF ou clique aqui'
        }
        helperText={
          newTemplate
            ? `Novo modelo: ${newTemplate.name} — ainda não salvo.`
            : certificate?.template?.original_name
              ? `Modelo atual: ${certificate.template.original_name}`
              : undefined
        }
        error={templateErrors.template}
        isRequired={!hasSavedTemplate}
        isDisabled={saving}
      />

      {page && page.pageCount > 1 && (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />O PDF tem {page.pageCount} páginas — só a primeira vai no certificado.
        </Alert>
      )}

      {page && layout && certificate && (
        <>
          <FormField label="Nome de exemplo" helperText="Só para a prévia — teste um nome longo." maxW="360px">
            <Input value={sampleName} onChange={(e) => setSampleName(e.target.value)} />
          </FormField>

          <CertificateLayoutEditor
            page={page}
            layout={layout}
            defaultLayout={scaleLayout(certificate.default_layout, REFERENCE_PAGE, page)}
            texts={texts}
            onChange={handleLayoutChange}
            isDisabled={saving}
          />
        </>
      )}

      {templateErrors.layout && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {templateErrors.layout}
        </Alert>
      )}
    </Flex>
  )

  const sendTab = !hasSavedTemplate ? (
    <Alert status="info" borderRadius="md" fontSize="sm">
      <AlertIcon />
      Salve um modelo de certificado na aba &quot;Modelo&quot; antes de enviar.
    </Alert>
  ) : (
    <>
      <Text variant="caption" mb={4}>
        Envia por e-mail o certificado de participação, em PDF, a cada participante que fez check-in no evento (com
        pagamento confirmado ou caução devolvida) — <strong>{eventName}</strong>.
      </Text>

      {pendingSchedule && (
        <Alert status="success" borderRadius="md" mb={4} alignItems="center" fontSize="sm">
          <AlertIcon />
          <Text fontSize="sm">
            Envio agendado para <strong>{formatLocalDateTime(pendingSchedule.scheduled_at)}</strong>. Altere os campos
            abaixo e salve para editar o agendamento.
          </Text>
          <Button size="sm" ml="auto" flexShrink={0} onClick={cancelScheduleDialog.onOpen} isDisabled={sending}>
            Cancelar agendamento
          </Button>
        </Alert>
      )}

      {!hasEventEnded && !pendingSchedule && endsAt && (
        <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />O evento termina em {formatLocalDateTime(endsAt)}. O envio imediato fica disponível a partir
          desse horário — até lá, agende o envio.
        </Alert>
      )}

      {isDirty && (
        <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />O modelo tem alterações não salvas. O envio usa o último modelo salvo.
        </Alert>
      )}

      {statsLoading && <LoadingBlock />}

      {!statsLoading && statsError && (
        <Alert status="error" borderRadius="md" alignItems="center">
          <AlertIcon />
          <Text fontSize="sm">Não foi possível carregar os destinatários.</Text>
          <Button size="sm" ml="auto" onClick={loadStats}>
            Tentar novamente
          </Button>
        </Alert>
      )}

      {!statsLoading && stats && (
        <>
          {usesSurveyLink && stats.survey_status !== 'active' && (
            <Alert status="warning" borderRadius="md" mb={4} alignItems="center" fontSize="sm">
              <AlertIcon />
              <Text fontSize="sm">
                {stats.survey_status === 'missing'
                  ? 'Este evento ainda não tem pesquisa NPS.'
                  : 'A pesquisa NPS está desativada.'}{' '}
                Ative a pesquisa ou remova #LINK_PESQUISA do corpo antes de enviar ou agendar os certificados.
              </Text>
              {onConfigureSurvey && (
                <Button
                  type="button"
                  size="sm"
                  ml="auto"
                  flexShrink={0}
                  onClick={() => {
                    handleClose()
                    onConfigureSurvey()
                  }}
                >
                  Configurar pesquisa
                </Button>
              )}
            </Alert>
          )}
          {usesSurveyLink && stats.survey_status === 'active' && (
            <Alert status="success" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />A pesquisa NPS está ativa. Cada destinatário receberá seu próprio link neste e-mail.
            </Alert>
          )}
          {scheduling && (
            <Text variant="caption" mb={3}>
              Números de agora. No envio agendado, os destinatários são calculados de novo, no horário do envio.
            </Text>
          )}

          <Flex gap={4} wrap="wrap">
            <StatCard
              flex="1"
              minW="180px"
              label="Participantes com check-in"
              value={stats.participants}
              color="abraPurple.500"
              icon="lni lni-users"
            />
            <StatCard
              flex="1"
              minW="180px"
              label="A enviar"
              value={stats.recipients}
              color="magenta.500"
              icon="lni lni-envelope"
            />
            <StatCard
              flex="1"
              minW="180px"
              label="Já receberam"
              value={stats.already_sent}
              color="abraGreen.500"
              icon="lni lni-checkmark-circle"
            />
          </Flex>

          {stats.missing_email > 0 && (
            <Alert status="warning" borderRadius="md" mt={4} fontSize="sm">
              <AlertIcon />
              {stats.missing_email} participante(s) sem e-mail cadastrado. Eles não receberão o certificado — preencha o
              e-mail e envie novamente.
            </Alert>
          )}

          {!scheduling && stats.recipients === 0 && (
            <Alert status="info" borderRadius="md" mt={4} fontSize="sm">
              <AlertIcon />
              Nenhum destinatário pendente.
              {stats.already_sent > 0 && ' Marque "reenviar" abaixo para enviar de novo a quem já recebeu.'}
            </Alert>
          )}
        </>
      )}

      <Divider my={4} />

      <Flex direction="column" gap={4}>
        <FormControl display="flex" alignItems="center" gap={3}>
          <Switch
            id="certificates-schedule-toggle"
            isChecked={scheduling}
            onChange={(e) => handleSchedulingChange(e.target.checked)}
            isDisabled={sending || !hasEventEnded}
          />
          <FormLabel htmlFor="certificates-schedule-toggle" mb={0} cursor="pointer" fontWeight="medium" fontSize="sm">
            Agendar envio
          </FormLabel>
        </FormControl>

        {scheduling && (
          <FormField
            label="Data e hora do envio"
            helperText={`${
              endsAt ? `A partir de ${formatLocalDateTime(endsAt)} (término do evento). ` : ''
            }Recebem o certificado os participantes com check-in no horário do envio.`}
            error={scheduledAtError}
            isRequired
            maxW="480px"
          >
            <Input
              type="datetime-local"
              value={scheduledAt}
              min={minScheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              isDisabled={sending}
            />
          </FormField>
        )}

        <FormField label="Assunto do e-mail" error={sendErrors.subject} isRequired>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255} isDisabled={sending} />
        </FormField>

        <FormField
          label="Corpo do e-mail"
          helperText={
            <Box as="span">
              Variáveis disponíveis: <strong>#PRIMEIRO_NOME</strong> (primeiro nome do participante),{' '}
              <strong>#NOME_EVENTO</strong> (nome do evento) e <strong>#LINK_PESQUISA</strong> (link individual da
              pesquisa NPS, que precisa estar ativa em &quot;Pesquisa NPS&quot;). O certificado vai anexado ao e-mail.
            </Box>
          }
          error={sendErrors.body}
          isRequired
        >
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            maxLength={10000}
            isDisabled={sending}
          />
        </FormField>
        <Button
          type="button"
          size="sm"
          variant="outline"
          alignSelf="flex-start"
          onClick={() => setShowEmailPreview((current) => !current)}
        >
          {showEmailPreview ? 'Ocultar exemplo do e-mail' : 'Ver exemplo do e-mail'}
        </Button>
        {showEmailPreview && (
          <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Exemplo para {SAMPLE_RECIPIENT_NAME} · cada pessoa recebe o próprio link
            </Text>
            <Text fontWeight="semibold" mb={3}>
              {sampleText(subject)}
            </Text>
            <Text whiteSpace="pre-wrap" fontSize="sm">
              {sampleText(body)}
            </Text>
          </Box>
        )}
      </Flex>
    </>
  )

  const footer =
    tabIndex === TAB_TEMPLATE ? (
      <Flex w="100%" justify="flex-end" gap={3} wrap="wrap">
        <Button
          variant="outline"
          type="button"
          onClick={handlePreview}
          isLoading={previewing}
          loadingText="Gerando..."
          isDisabled={!page || !layout || previewing}
        >
          <Icon name="lni lni-eye" mr="6px" />
          Pré-visualizar PDF
        </Button>
        <Button
          variant="magenta"
          type="button"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Salvando..."
          isDisabled={!page || !layout || !isDirty}
        >
          Salvar modelo
        </Button>
      </Flex>
    ) : (
      <Flex w="100%" align="center" gap={3} wrap="wrap">
        <Checkbox
          mr="auto"
          isChecked={force}
          onChange={(e) => setForce(e.target.checked)}
          isDisabled={sending || !hasSavedTemplate}
        >
          <Text fontSize="sm">Reenviar para quem já recebeu</Text>
        </Checkbox>
        <Button
          variant="magenta"
          type="button"
          onClick={confirmDialog.onOpen}
          isLoading={sending}
          loadingText={scheduling ? 'Agendando...' : 'Enviando...'}
          isDisabled={!canSend}
        >
          {scheduling ? (pendingSchedule ? 'Salvar agendamento' : 'Agendar envio') : 'Enviar certificados'}
        </Button>
      </Flex>
    )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Certificados de participação"
        size="editor"
        tabIndex={tabIndex}
        onTabChange={setTabIndex}
        footer={footer}
        tabs={[
          { label: 'Modelo', content: loading ? <LoadingBlock /> : templateTab },
          { label: 'Envio', content: loading ? <LoadingBlock /> : sendTab }
        ]}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        onConfirm={handleSend}
        action="send"
        title={scheduling ? 'Confirmar agendamento dos certificados' : 'Confirmar envio de certificados'}
        description={
          scheduling
            ? `Deseja agendar o envio do certificado de participação para ${formatLocalDateTime(scheduledAt)}? ` +
              `Recebem os participantes com check-in nesse horário${force ? ', INCLUINDO quem já recebeu' : ' que ainda não receberam'}.`
            : `Deseja enviar o certificado de participação para ${stats?.recipients ?? 0} participante(s)${
                force ? ', INCLUINDO quem já recebeu' : ''
              }? Esta ação não pode ser desfeita.`
        }
        confirmLabel={scheduling ? 'Agendar' : 'Enviar'}
        isLoading={sending}
      />
      <ConfirmDialog
        isOpen={cancelScheduleDialog.isOpen}
        onClose={cancelScheduleDialog.onClose}
        onConfirm={handleCancelSchedule}
        action="delete"
        irreversible={false}
        title="Cancelar agendamento"
        description={`Deseja cancelar o envio de certificados agendado para ${formatLocalDateTime(
          pendingSchedule?.scheduled_at
        )}? Os certificados não serão enviados nesse horário.`}
        confirmLabel="Cancelar agendamento"
        cancelLabel="Voltar"
      />
    </>
  )
}
